import { useApolloClient } from '@apollo/client';
import UserDataQuery from './UserDataQuery';
import { formatIsoDate } from '../../Helpers/dateFormatter';
import removeAuthCredentials from '../../Auth/removeAuthCredentials';

const isUnauthorizedError = (err) => {
  const statusCode =
    err?.networkError?.statusCode ?? err?.networkError?.response?.status ?? err?.statusCode;
  if (statusCode === 401) return true;

  const graphQLErrors = err?.graphQLErrors ?? [];
  if (
    graphQLErrors.some(
      (e) =>
        e?.extensions?.code === 'UNAUTHENTICATED' ||
        e?.extensions?.code === 'UNAUTHORIZED' ||
        e?.code === 'UNAUTHENTICATED' ||
        e?.code === 'UNAUTHORIZED'
    )
  ) {
    return true;
  }

  // Backend returns 401 without CORS headers, so the browser blocks the
  // response and Apollo only sees `TypeError: Failed to fetch`. Treat this as
  // unauthorized when we had a token and the browser thinks we're online.
  const networkError = err?.networkError;
  const hasToken = typeof window !== 'undefined' && !!window.localStorage?.getItem('token');
  const isOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
  if (
    hasToken &&
    isOnline &&
    networkError?.name === 'TypeError' &&
    /failed to fetch/i.test(networkError?.message ?? '')
  ) {
    return true;
  }

  return false;
};

const UserDataConnectorApollo = ({ children }) => {
  const client = useApolloClient();

  const fetch = async () => {
    try {
      const date = formatIsoDate(new Date());

      const result = await client.query({
        query: UserDataQuery,
        variables: { date },
        fetchPolicy: 'network-only',
      });

      const email = result?.data?.me?.email ?? '';
      const id = result?.data?.me?.id ?? '';
      const systemOfMeasures = result?.data?.me?.systemOfMeasures ?? 'si';
      const systemOfMeasuresNutrition = result?.data?.me?.systemOfMeasuresNutrition ?? 'si';
      localStorage.setItem('userEmail', email);
      localStorage.setItem('userId', id);
      localStorage.setItem('systemOfMeasures', systemOfMeasures);
      localStorage.setItem('systemOfMeasuresNutrition', systemOfMeasuresNutrition);
      localStorage.setItem('userCreated', result?.data?.me?.created ?? '');

      // check if user has access to diet, ai and workout
      const todayDateString = new Date().toISOString().slice(0, 10);
      const dietAccessTo = result?.data?.me?.diet?.accessTo;
      const hasDietAccess = Boolean(dietAccessTo) && dietAccessTo >= todayDateString;

      const workoutAccessTo = result?.data?.me?.videoWorkout?.accessTo;
      const hasWorkoutAccess = Boolean(workoutAccessTo) && workoutAccessTo >= todayDateString;

      const aiAccess = result?.data?.me?.aiAccess?.hasAccess;
      const dietAccess = hasDietAccess;
      // Written because `workoutAccess` was read in TrackerNoAccess but never set by anything, so
      // it always evaluated as "no access".
      const workoutAccess = hasWorkoutAccess;

      sessionStorage.setItem('aiAccess', aiAccess ? 'true' : 'false');
      sessionStorage.setItem('dietAccess', dietAccess ? 'true' : 'false');
      sessionStorage.setItem('workoutAccess', workoutAccess ? 'true' : 'false');

      // The dates behind those booleans, kept for developer options. Without them a false reads the
      // same whether the plan lapsed or was never bought, which is the distinction access reports
      // usually turn on. Nothing else reads these.
      sessionStorage.setItem('dietAccessTo', dietAccessTo ?? '');
      sessionStorage.setItem('workoutAccessTo', workoutAccessTo ?? '');

      return result;
    } catch (err) {
      if (isUnauthorizedError(err)) {
        removeAuthCredentials();
        window.location.reload();
        return;
      }
      console.error('Failed to fetch user data', err);
      throw err;
    }
  };

  return children({
    userDataOnDemand: fetch,
  });
};

export default UserDataConnectorApollo;
